/**
 * @lms/i18n — 언어·공용 사전·용어 토큰(§16-18).
 *
 * **한 파일이다** — 내부 상대 import 가 있으면 소비자마다 해석기가 달라 전부 걸린다
 * (API 는 tsc nodenext 라 확장자를 요구하고, Next(Turbopack)는 `.js`→`.ts` 매핑을 안 하고,
 * Vite·tsx 는 둘 다 된다). 188줄이라 합치는 쪽이 어떤 설정보다 싸다(2026-08-21).
 */
/** 지원 언어 (§16-3) — 4개국어: 한국어/영어/일본어/중국어(대만, zh-tw). 전 앱 공용. */
export type Lang = 'ko' | 'en' | 'ja' | 'zh-tw';

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'ko', label: '한국어' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'zh-tw', label: '繁體中文' },
];

export const LANG_CODES: Lang[] = LANGS.map((l) => l.code);

/** 관리자 기본 한국어 / 수강생 프론트 기본 영어(§16-3). 앱이 필요 값 선택 */
export const DEFAULT_ADMIN: Lang = 'ko';
export const DEFAULT_WEB: Lang = 'en';

export type Dict = Record<string, string>;
export type MultiDict = Record<Lang, Dict>;

/** 여러 사전 병합 (공유 + 앱 UI) — 뒤 사전이 우선 */
export function mergeDicts(...dicts: MultiDict[]): MultiDict {
  const out = { ko: {}, en: {}, ja: {}, 'zh-tw': {} } as MultiDict;
  for (const d of dicts) for (const lang of LANG_CODES) Object.assign(out[lang], d[lang]);
  return out;
}

// ══ 공용 사전 ══
/**
 * 전 앱 공용 사전 — 공통 액션/단위 + **도메인 enum 라벨**(DB enum: 회원 상태·등급, 주문 상태·형태,
 * 수강 유형·상태, 쿠폰 상태, 포인트 구분, 상품 유형 등). admin/web/api 어디서든 동일 표기 보장.
 * 앱 고유 UI 문자열(메뉴·대시보드 등)은 각 앱 사전에.
 */
export const SHARED: MultiDict = {
  ko: {
    // 공통 액션/상태
    'action.search': '조회', 'action.reset': '초기화', 'action.save': '저장', 'action.cancel': '취소', 'action.edit': '수정',
    'action.download': 'XLS 다운로드', 'action.back': '목록으로', 'action.logout': '로그아웃',
    'common.market': '마켓', 'common.marketFirst': '마켓을 먼저 선택해 주세요.', 'common.market.all': '전체', 'common.loading': '불러오는 중…',
    'common.total': '총', 'common.count': '건', 'common.none': '조회된 내역이 없습니다',
    'common.language': '언어', 'common.saved': '저장되었습니다', 'common.search': '검색', 'common.searchDetail': '상세검색', 'common.preparing': '준비 중입니다', 'common.notFound': '찾을 수 없습니다. 다른 사이트의 자료이거나 삭제된 자료입니다.',
    'unit.won': '원', 'unit.people': '명',
    // 회원
    'member.status.ACTIVE': '정상', 'member.status.DORMANT': '휴면', 'member.status.BLOCKED': '차단',
    'member.status.WITHDRAWN': '탈퇴', 'member.status.DELETED': '삭제',
    'member.role.STUDENT': '일반', 'member.role.TEACHER': '강사', 'member.role.ADMIN': '관리자',
    // 주문
    'order.otype.NORMAL': '일반', 'order.otype.BILLING': '구독',
    'order.ostatus.PAID': '결제완료', 'order.ostatus.CANCELED': '취소', 'order.ostatus.IN_PROGRESS': '진행', 'order.ostatus.AWAITING_DEPOSIT': '입금대기',
    // 수강
    'enroll.ptype.FREEPASS': '프리패스', 'enroll.ptype.PACKAGE': '패키지', 'enroll.ptype.LECTURE': '단과', 'enroll.ptype.BOOK': '교재',
    'enroll.pstatus.WAITING': '대기', 'enroll.pstatus.IN_PROGRESS': '수강중', 'enroll.pstatus.PAUSED': '정지', 'enroll.pstatus.EXPIRED': '만료', 'enroll.pstatus.CANCELED': '취소',
    // 포인트/쿠폰
    'point.ptype.SAVE': '적립', 'point.ptype.USE': '사용', 'point.ptype.EXPIRE': '만료', 'point.ptype.ADJUST': '정정',
    'coupon.cstatus.UNUSED': '미사용', 'coupon.cstatus.USED': '사용', 'coupon.cstatus.EXPIRED': '만료',
  },
  en: {
    'action.search': 'Search', 'action.reset': 'Reset', 'action.save': 'Save', 'action.cancel': 'Cancel', 'action.edit': 'Edit',
    'action.download': 'Download XLS', 'action.back': 'Back to list', 'action.logout': 'Logout',
    'common.market': 'Market', 'common.marketFirst': 'Select a market first.', 'common.market.all': 'All', 'common.loading': 'Loading…',
    'common.total': 'Total', 'common.count': '', 'common.none': 'No data found',
    'common.language': 'Language', 'common.saved': 'Saved', 'common.search': 'Search', 'common.searchDetail': 'Search', 'common.preparing': 'Coming soon', 'common.notFound': 'Not found. It may belong to another site or have been deleted.',
    'unit.won': ' KRW', 'unit.people': '',
    'member.status.ACTIVE': 'Active', 'member.status.DORMANT': 'Dormant', 'member.status.BLOCKED': 'Blocked',
    'member.status.WITHDRAWN': 'Withdrawn', 'member.status.DELETED': 'Deleted',
    'member.role.STUDENT': 'Student', 'member.role.TEACHER': 'Teacher', 'member.role.ADMIN': 'Admin',
    'order.otype.NORMAL': 'Normal', 'order.otype.BILLING': 'Subscription',
    'order.ostatus.PAID': 'Paid', 'order.ostatus.CANCELED': 'Canceled', 'order.ostatus.IN_PROGRESS': 'In progress', 'order.ostatus.AWAITING_DEPOSIT': 'Awaiting',
    'enroll.ptype.FREEPASS': 'Freepass', 'enroll.ptype.PACKAGE': 'Package', 'enroll.ptype.LECTURE': 'Lecture', 'enroll.ptype.BOOK': 'Book',
    'enroll.pstatus.WAITING': 'Waiting', 'enroll.pstatus.IN_PROGRESS': 'Active', 'enroll.pstatus.PAUSED': 'Paused', 'enroll.pstatus.EXPIRED': 'Expired', 'enroll.pstatus.CANCELED': 'Canceled',
    'point.ptype.SAVE': 'Save', 'point.ptype.USE': 'Use', 'point.ptype.EXPIRE': 'Expire', 'point.ptype.ADJUST': 'Adjust',
    'coupon.cstatus.UNUSED': 'Unused', 'coupon.cstatus.USED': 'Used', 'coupon.cstatus.EXPIRED': 'Expired',
  },
  ja: {
    'action.search': '照会', 'action.reset': 'リセット', 'action.save': '保存', 'action.cancel': 'キャンセル', 'action.edit': '修正',
    'action.download': 'XLSダウンロード', 'action.back': '一覧へ', 'action.logout': 'ログアウト',
    'common.market': 'マーケット', 'common.marketFirst': '先にマーケットを選択してください。', 'common.market.all': '全体', 'common.loading': '読み込み中…',
    'common.total': '合計', 'common.count': '件', 'common.none': '照会された内訳がありません',
    'common.language': '言語', 'common.saved': '保存されました', 'common.search': '検索', 'common.searchDetail': '詳細検索', 'common.preparing': '準備中です', 'common.notFound': '見つかりません。他サイトの資料か、削除された資料です。',
    'unit.won': 'ウォン', 'unit.people': '名',
    'member.status.ACTIVE': '正常', 'member.status.DORMANT': '休眠', 'member.status.BLOCKED': 'ブロック',
    'member.status.WITHDRAWN': '退会', 'member.status.DELETED': '削除',
    'member.role.STUDENT': '一般', 'member.role.TEACHER': '講師', 'member.role.ADMIN': '管理者',
    'order.otype.NORMAL': '一般', 'order.otype.BILLING': 'サブスク',
    'order.ostatus.PAID': '決済完了', 'order.ostatus.CANCELED': '取消', 'order.ostatus.IN_PROGRESS': '進行', 'order.ostatus.AWAITING_DEPOSIT': '入金待ち',
    'enroll.ptype.FREEPASS': 'フリーパス', 'enroll.ptype.PACKAGE': 'パッケージ', 'enroll.ptype.LECTURE': '単科', 'enroll.ptype.BOOK': '教材',
    'enroll.pstatus.WAITING': '待機', 'enroll.pstatus.IN_PROGRESS': '受講中', 'enroll.pstatus.PAUSED': '停止', 'enroll.pstatus.EXPIRED': '満了', 'enroll.pstatus.CANCELED': '取消',
    'point.ptype.SAVE': '付与', 'point.ptype.USE': '使用', 'point.ptype.EXPIRE': '満了', 'point.ptype.ADJUST': '訂正',
    'coupon.cstatus.UNUSED': '未使用', 'coupon.cstatus.USED': '使用', 'coupon.cstatus.EXPIRED': '満了',
  },
  'zh-tw': {
    'action.search': '查詢', 'action.reset': '重設', 'action.save': '儲存', 'action.cancel': '取消', 'action.edit': '編輯',
    'action.download': '下載 XLS', 'action.back': '返回列表', 'action.logout': '登出',
    'common.market': '市場', 'common.marketFirst': '請先選擇市場。', 'common.market.all': '全部', 'common.loading': '載入中…',
    'common.total': '共', 'common.count': '筆', 'common.none': '查無資料',
    'common.language': '語言', 'common.saved': '已儲存', 'common.search': '搜尋', 'common.searchDetail': '詳細搜尋', 'common.preparing': '準備中', 'common.notFound': '找不到。可能屬於其他網站或已刪除。',
    'unit.won': '元', 'unit.people': '人',
    'member.status.ACTIVE': '正常', 'member.status.DORMANT': '休眠', 'member.status.BLOCKED': '封鎖',
    'member.status.WITHDRAWN': '退會', 'member.status.DELETED': '刪除',
    'member.role.STUDENT': '一般', 'member.role.TEACHER': '講師', 'member.role.ADMIN': '管理員',
    'order.otype.NORMAL': '一般', 'order.otype.BILLING': '訂閱',
    'order.ostatus.PAID': '已付款', 'order.ostatus.CANCELED': '取消', 'order.ostatus.IN_PROGRESS': '進行', 'order.ostatus.AWAITING_DEPOSIT': '待匯款',
    'enroll.ptype.FREEPASS': '暢學方案', 'enroll.ptype.PACKAGE': '套裝', 'enroll.ptype.LECTURE': '單科', 'enroll.ptype.BOOK': '教材',
    'enroll.pstatus.WAITING': '等待', 'enroll.pstatus.IN_PROGRESS': '上課中', 'enroll.pstatus.PAUSED': '暫停', 'enroll.pstatus.EXPIRED': '到期', 'enroll.pstatus.CANCELED': '取消',
    'point.ptype.SAVE': '累積', 'point.ptype.USE': '使用', 'point.ptype.EXPIRE': '到期', 'point.ptype.ADJUST': '校正',
    'coupon.cstatus.UNUSED': '未使用', 'coupon.cstatus.USED': '已使用', 'coupon.cstatus.EXPIRED': '到期',
  },
};

// ══ 용어 토큰 (§16-18) ══
/**
 * 용어 커스터마이징 (§16-18, 2026-08-05 확정 — 프론트 착수와 함께 구현).
 *
 * 고객사(마켓)마다 도메인 단어를 다르게 부른다("강사"→교수, "교재"→북). 번역 문자열에
 * 단어를 하드코딩하지 않고 `{teacher}` 같은 토큰으로 두고, 마켓 `terms`(JSON) 사전으로
 * 치환한다. 슬롯은 시스템 정의 14종 고정(자유 추가 불가) — 기본값·표기는 §16-18 표가 원본.
 *
 * 폴백 — `terms[slot][lang]` → 시스템 기본(TERM_DEFAULTS). 언어 단위로도 폴백한다
 * (ko 만 바꾸면 en/ja/zh-tw 는 기본 유지). 강좌명·강사명 같은 데이터 원문은 대상이 아니다.
 */
export const TERM_SLOTS = [
  'teacher', 'student', 'course', 'lesson', 'enrollment', 'review',
  'lecture', 'package', 'freepass', 'textbook', 'point', 'coupon', 'exam', 'wishlist',
] as const;
export type TermSlot = (typeof TERM_SLOTS)[number];

/** 마켓 오버라이드 형태 — 설정한 슬롯·언어만 담긴다(부분 오버라이드) */
export type MarketTerms = Partial<Record<TermSlot, Partial<Record<Lang, string>>>>;

/** 시스템 기본 사전 (§16-18 표 그대로) — DB 시드 없이 코드가 기본을 안다 */
export const TERM_DEFAULTS: Record<TermSlot, Record<Lang, string>> = {
  teacher: { ko: '강사', en: 'Instructor', ja: '講師', 'zh-tw': '講師' },
  student: { ko: '회원', en: 'Member', ja: '会員', 'zh-tw': '會員' },
  course: { ko: '강좌', en: 'Course', ja: '講座', 'zh-tw': '課程' },
  // 2026-08-27 사용자 확정 — ko 는 `강의`(사전 다수가 이미 그렇다), ja 는 `講義`.
  // `講` 한 글자는 다른 말에 너무 많이 끼어(오탐 197건) 자동 치환을 못 한다.
  lesson: { ko: '강의', en: 'Lesson', ja: '講義', 'zh-tw': '單元' },
  enrollment: { ko: '수강', en: 'Learning', ja: '受講', 'zh-tw': '上課' },
  review: { ko: '수강후기', en: 'Review', ja: '受講レビュー', 'zh-tw': '課程評價' },
  lecture: { ko: '단과', en: 'Single Course', ja: '単科', 'zh-tw': '單科' },
  package: { ko: '패키지', en: 'Package', ja: 'パッケージ', 'zh-tw': '套裝' },
  freepass: { ko: '프리패스', en: 'Free Pass', ja: 'フリーパス', 'zh-tw': '無限暢學' },
  textbook: { ko: '교재', en: 'Textbook', ja: '教材', 'zh-tw': '教材' },
  point: { ko: '포인트', en: 'Points', ja: 'ポイント', 'zh-tw': '點數' },
  coupon: { ko: '쿠폰', en: 'Coupon', ja: 'クーポン', 'zh-tw': '優惠券' },
  exam: { ko: '시험', en: 'Exam', ja: '試験', 'zh-tw': '測驗' },
  wishlist: { ko: '찜한 상품', en: 'Wishlist', ja: 'お気に入り', 'zh-tw': '收藏' },
};

/**
 * 후보 세트 (§16-18 표의 "후보 세트" 열 — 2026-08-26 구현).
 *
 * 설정 화면의 select 소스다. **4개국어를 시스템이 채워 주는 것이 요점** — 고객사가 "교수" 를
 * 고르면 en/ja/zh-tw 도 함께 정해져 번역 걱정이 없다. 여기 없는 단어는 4개국어를 직접 넣는다.
 *
 * 기본값(TERM_DEFAULTS)은 여기 넣지 않는다 — 화면이 맨 위에 "기본" 항목으로 따로 세운다.
 */
export const TERM_PRESETS: Record<TermSlot, Record<Lang, string>[]> = {
  teacher: [
    { ko: '선생님', en: 'Teacher', ja: '先生', 'zh-tw': '老師' },
    { ko: '교수', en: 'Professor', ja: '教授', 'zh-tw': '教授' },
    { ko: '멘토', en: 'Mentor', ja: 'メンター', 'zh-tw': '導師' },
    { ko: '코치', en: 'Coach', ja: 'コーチ', 'zh-tw': '教練' },
    { ko: '튜터', en: 'Tutor', ja: 'チューター', 'zh-tw': '家教' },
  ],
  student: [
    { ko: '수강생', en: 'Student', ja: '受講生', 'zh-tw': '學員' },
    { ko: '학생', en: 'Student', ja: '学生', 'zh-tw': '學生' },
    { ko: '학습자', en: 'Learner', ja: '学習者', 'zh-tw': '學習者' },
    { ko: '훈련생', en: 'Trainee', ja: '訓練生', 'zh-tw': '受訓者' },
  ],
  course: [
    // `강의`·`講義` 는 lesson 의 표준어라 후보에서 뺐다(2026-08-27) — 두 슬롯이 같은 말을
    // 두면 자동 치환이 서로를 먹는다. 강좌를 다르게 부르려면 아래 셋에서 고른다.
    { ko: '교육', en: 'Training', ja: '教育', 'zh-tw': '教育' },
    { ko: '클래스', en: 'Class', ja: 'クラス', 'zh-tw': '課堂' },
    { ko: '코스', en: 'Course', ja: 'コース', 'zh-tw': '課程' },
    { ko: '과정', en: 'Program', ja: '課程', 'zh-tw': '課程' },
  ],
  lesson: [
    { ko: '강', en: 'Lesson', ja: '講', 'zh-tw': '講' },
    { ko: '회차', en: 'Session', ja: '回', 'zh-tw': '堂次' },
    { ko: '레슨', en: 'Lesson', ja: 'レッスン', 'zh-tw': '課程單元' },
    { ko: '커리큘럼', en: 'Curriculum', ja: 'カリキュラム', 'zh-tw': '課綱' },
  ],
  enrollment: [
    { ko: '학습', en: 'Study', ja: '学習', 'zh-tw': '學習' },
    { ko: '훈련', en: 'Training', ja: '訓練', 'zh-tw': '訓練' },
  ],
  review: [
    { ko: '후기', en: 'Review', ja: 'レビュー', 'zh-tw': '評價' },
    { ko: '리뷰', en: 'Review', ja: 'レビュー', 'zh-tw': '評論' },
    { ko: '수강평', en: 'Feedback', ja: '受講評', 'zh-tw': '課程回饋' },
  ],
  lecture: [
    { ko: '개별강좌', en: 'Individual Course', ja: '個別講座', 'zh-tw': '單堂課程' },
    { ko: '싱글', en: 'Single', ja: 'シングル', 'zh-tw': '單堂' },
  ],
  package: [
    { ko: '묶음', en: 'Bundle', ja: 'まとめ', 'zh-tw': '組合' },
    { ko: '세트', en: 'Set', ja: 'セット', 'zh-tw': '套組' },
    { ko: '번들', en: 'Bundle', ja: 'バンドル', 'zh-tw': '套組' },
  ],
  freepass: [
    { ko: '풀패키지', en: 'Full Package', ja: 'フルパッケージ', 'zh-tw': '全套方案' },
    { ko: '무제한 수강권', en: 'Unlimited Pass', ja: '無制限受講券', 'zh-tw': '無限暢學券' },
  ],
  textbook: [
    { ko: '책', en: 'Book', ja: '本', 'zh-tw': '書籍' },
    { ko: '도서', en: 'Book', ja: '図書', 'zh-tw': '圖書' },
    { ko: '북', en: 'Book', ja: 'ブック', 'zh-tw': '書' },
  ],
  point: [
    { ko: '적립금', en: 'Credit', ja: '積立金', 'zh-tw': '回饋金' },
    { ko: '캐시', en: 'Cash', ja: 'キャッシュ', 'zh-tw': '現金金幣' },
    { ko: '마일리지', en: 'Mileage', ja: 'マイレージ', 'zh-tw': '哩程' },
  ],
  coupon: [
    { ko: '할인권', en: 'Discount Ticket', ja: '割引券', 'zh-tw': '折價券' },
    { ko: '바우처', en: 'Voucher', ja: 'バウチャー', 'zh-tw': '兌換券' },
    { ko: '이용권', en: 'Pass', ja: '利用券', 'zh-tw': '使用券' },
  ],
  exam: [
    { ko: '모의고사', en: 'Mock Exam', ja: '模擬試験', 'zh-tw': '模擬考' },
    { ko: '테스트', en: 'Test', ja: 'テスト', 'zh-tw': '測試' },
    { ko: '평가', en: 'Assessment', ja: '評価', 'zh-tw': '評量' },
  ],
  wishlist: [
    { ko: '관심상품', en: 'Favorites', ja: '関心商品', 'zh-tw': '興趣商品' },
    { ko: '위시리스트', en: 'Wishlist', ja: 'ウィッシュリスト', 'zh-tw': '願望清單' },
    { ko: '즐겨찾기', en: 'Bookmarks', ja: 'ブックマーク', 'zh-tw': '我的收藏' },
  ],
};

/**
 * 조사쌍(ko 전용) — 치환된 단어의 **받침 유무로 자동 선택**한다.
 * `{teacher:이가}` → 강사"가" / 선생님"이". 다른 언어는 조사를 버리고 단어만 넣는다.
 */
const JOSA: Record<string, [string, string]> = {
  이가: ['이', '가'],
  을를: ['을', '를'],
  은는: ['은', '는'],
  과와: ['과', '와'],
  으로로: ['으로', '로'],
};

/** 받침 판정 — 한글 음절이 아니면(영문 등) 받침 없음으로 본다(가장 자연스러운 쪽) */
const hasBatchim = (word: string): boolean => {
  const code = word.charCodeAt(word.length - 1) - 0xac00;
  return code >= 0 && code <= 11171 && code % 28 > 0;
};

/** 받침이 ㄹ 인가 — `으로/로` 만 예외로 본다(서울"로", 서울"으로" 가 아니다) */
const isRieul = (word: string): boolean => {
  const code = word.charCodeAt(word.length - 1) - 0xac00;
  return code >= 0 && code <= 11171 && code % 28 === 8;
};

/** 단어에 맞는 조사 하나 고르기 — 조사쌍 토큰과 자동 교정이 함께 쓴다 */
function pickJosa(word: string, pair: string): string {
  const [withB, withoutB] = JOSA[pair];
  if (pair === '으로로' && isRieul(word)) return withoutB; // ㄹ 받침은 '로'
  return hasBatchim(word) ? withB : withoutB;
}

const TOKEN_RE = new RegExp(`\\{(${TERM_SLOTS.join('|')})(?::(${Object.keys(JOSA).join('|')}))?\\}`, 'g');

/**
 * 토큰 치환 — 번역 문자열의 `{slot}`·`{slot:조사쌍}` 을 그 마켓의 단어로 바꾼다.
 * t() 뒤에 후처리로 부른다(치환할 토큰이 없으면 문자열을 그대로 돌려준다 — 비용 없음).
 */
export function applyTerms(text: string, lang: Lang, terms?: MarketTerms | null): string {
  const withTokens = text.includes('{')
    ? text.replace(TOKEN_RE, (_, slot: TermSlot, josa?: string) => {
      const word = terms?.[slot]?.[lang] ?? TERM_DEFAULTS[slot][lang];
      return lang === 'ko' && josa ? word + pickJosa(word, josa) : word;
    })
    : text;
  return autoTerms(withTokens, lang, terms);
}

/**
 * **자동 치환 대상 슬롯**(2026-08-26 사용자 확정 — "명확한 것만").
 *
 * 뜻이 하나뿐이고 다른 말의 일부로 끼지 않는 것만 넣는다. 4개국어 전수 스캔으로 확인했다 —
 * 영어 단어경계 위반 0건, 한국어 합성어 후보 1건(`적용가능쿠폰없음`, 그것도 정상 치환).
 *
 * **여기 없는 5종은 넣지 않는다** —
 *  · `lesson` 강의 : 2026-08-27 사용자 확정 — 강의는 **소스 표기 통일**(커리큘럼·차시 혼용 정리)
 *    이지 고객사가 바꾸는 용어가 아니다. 승격하려면 별칭(차시)과 비켜갈 자리(수강의·강의실)
 *    처리가 필요하다 — 설계 문서 §3 에 그 지식을 남겨 두었다
 *  · `student` 회원 : `회원가입`·`비회원` 처럼 **계정** 뜻으로도 쓴다(학습자 뜻과 한 단어)
 *  · `enrollment` 수강 : `수강확인서` 같이 바꾸면 안 되는 말을 품는다
 *  · `review` 수강후기 : `enrollment` 와 겹쳐 최장일치가 필요하다
 *  · `wishlist` : 기본값이 구(句) `찜한 상품` 이라 활용이 안 된다(사전 실제 표기는 `찜`)
 */
export const AUTO_SLOTS: readonly TermSlot[] = [
  'teacher', 'course', 'lecture', 'package', 'freepass', 'textbook', 'point', 'coupon', 'exam',
];

/**
 * 뒤따르는 조사 — 단어가 바뀌면 받침이 달라지므로 함께 고쳐야 한다(강사"가" → 선생님"이").
 *
 * **뒤에 한글이 더 오면 조사가 아니다**(`(?![가-힣])`) — `교재과정` 의 `과` 를 조사로 보면
 * `도서와정` 이 된다. 조사는 낱말 끝에만 붙으므로 이 한 줄이 그 사고를 막는다.
 *
 * 전체가 **선택**(`(?:…)?`)이라는 점이 중요하다 — `?` 를 전방탐색 뒤에 붙이면 조사 그룹이
 * 필수가 되어 `강사 관리` 처럼 조사 없는 자리를 통째로 놓친다(2026-08-26 테스트가 잡았다).
 */
const JOSA_AFTER = '(?:(으로|이|가|을|를|은|는|과|와|로)(?![가-힣]))?';
const JOSA_PAIR: Record<string, string> = {
  이: '이가', 가: '이가', 을: '을를', 를: '을를',
  은: '은는', 는: '은는', 과: '과와', 와: '과와', 으로: '으로로', 로: '으로로',
};

/** 정규식 특수문자 탈출 — 기본 단어에 `.` 같은 글자가 들어와도 안전하게 */
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * **자동 치환** — 토큰이 없어도 기본 단어 자체를 찾아 바꾼다(2026-08-26 사용자 요구).
 *
 * 토큰 방식만 두면 앞으로 쓰는 문자열마다 개발자가 `{teacher}` 를 심어야 하고, 한 번이라도
 * 잊으면 그 화면만 옛 단어로 남는다. 그래서 **기본 단어를 아는 우리가 런타임에 바꾼다** —
 * 새로 짜는 코드가 `'강사 목록'` 이라고 써도 저절로 `'교수 목록'` 이 된다.
 *
 * 안전은 셋이 만든다 —
 *  ① **바꾼 슬롯만** 훑는다. 안 바꾼 마켓은 첫 줄에서 되돌아가므로 비용이 없다
 *  ② 영어는 **단어 경계**를 본다(`Coursework` 의 `Course` 는 건드리지 않는다).
 *     한국어·일본어·중국어는 띄어쓰기가 뜻의 경계가 아니라 경계를 걸지 않는다 —
 *     대신 AUTO_SLOTS 를 뜻이 겹치지 않는 단어로만 좁혀 두었다
 *  ③ 바뀐 단어의 받침으로 **뒤따르는 조사를 다시 고른다**
 */
export function autoTerms(text: string, lang: Lang, terms?: MarketTerms | null): string {
  if (!terms || !text) return text;
  let out = text;
  for (const slot of AUTO_SLOTS) {
    const word = terms[slot]?.[lang];
    const base = TERM_DEFAULTS[slot][lang];
    if (!word || word === base) continue; // 안 바꿨으면 훑지 않는다
    if (lang === 'en') {
      out = out.replace(new RegExp(`\\b${esc(base)}\\b`, 'g'), word);
    } else if (lang === 'ko') {
      // 단어와 **뒤따르는 조사까지 한 번에** 잡아 새 단어의 받침으로 조사를 다시 고른다
      out = out.replace(new RegExp(`${esc(base)}${JOSA_AFTER}`, 'g'),
        (_m, josa?: string) => (josa ? word + pickJosa(word, JOSA_PAIR[josa]) : word));
    } else {
      out = out.replace(new RegExp(esc(base), 'g'), word);
    }
  }
  return out;
}


/** 슬롯 하나의 현재 단어 — 문장 밖에서 단어만 필요할 때(메뉴 라벨 등) */
export function termWord(slot: TermSlot, lang: Lang, terms?: MarketTerms | null): string {
  return terms?.[slot]?.[lang] ?? TERM_DEFAULTS[slot][lang];
}
